import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ExternalDataService } from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { PhoneFormatterService } from '../../../services/phone-formatter.service';
import { Observable, filter, take } from 'rxjs';

@Component({
  selector: 'app-create-customer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './create-customer.component.html',
  styleUrl: './create-customer.component.css'
})
export class CreateCustomerComponent implements OnInit {
  customerForm: FormGroup;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    private phoneFormatter: PhoneFormatterService,
    private router: Router
  ) {
    this.customerForm = this.createForm();
  }

  ngOnInit(): void {
    // Wait for MSAL to be initialized before any operations
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe();
  }

  // Indian phone number validator
  private indianPhoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    const cleanNumber = control.value.replace(/\D/g, '');
    // Indian mobile numbers: 10 digits starting with 6,7,8,9 or with country code +91
    if (!/^(\+91|91)?[6-9]\d{9}$/.test(cleanNumber)) {
      return { indianPhone: { message: 'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)' } };
    }
    
    return null;
  }

  // Format phone number as user types (Indian format)
  onPhoneNumberInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    
    // Handle country code +91
    if (value.startsWith('91') && value.length > 2) {
      value = '+91 ' + value.substring(2);
    } else if (value.startsWith('91') && value.length <= 2) {
      value = '+91';
    } else if (value.length > 0 && !value.startsWith('91')) {
      // Format Indian mobile number: +91 XXXXX XXXXX
      if (value.length > 5) {
        value = '+91 ' + value.substring(0, 5) + ' ' + value.substring(5, 10);
      } else if (value.length > 0) {
        value = '+91 ' + value;
      }
    }
    
    // Limit to 10 digits (excluding country code)
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length > 12) { // +91 + 10 digits
      return; // Don't update if too long
    }
    
    // Update the form control value
    this.customerForm.get('phoneNumber')?.setValue(value);
  }

  private createForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', [Validators.required, this.indianPhoneValidator]],
      customerAddress: this.fb.array([])
    });
  }

  get addresses(): FormArray<FormGroup> {
    return this.customerForm.get('customerAddress') as FormArray<FormGroup>;
  }

  addAddress(): void {
    const addressForm = this.fb.group({
      address: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      locationName: ['', Validators.required],
      isPrimary: [false],
      latitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]],
      addressType: ['']
    });

    this.addresses.push(addressForm);
  }

  removeAddress(index: number): void {
    this.addresses.removeAt(index);
  }

  setPrimaryAddress(index: number): void {
    // Set all addresses to non-primary first
    this.addresses.controls.forEach((control, i) => {
      control.patchValue({ isPrimary: i === index });
    });
  }

  onSubmit(): void {
    if (this.customerForm.valid) {
      this.isSubmitting = true;
      
      const formValue = this.customerForm.value;
      const customerData = {
        name: formValue.name,
        phoneNumber: this.phoneFormatter.getStorageFormat(formValue.phoneNumber),
        customerAddress: formValue.customerAddress.map((addr: any) => ({
          address: addr.address,
          pincode: addr.pincode,
          locationName: addr.locationName,
          isPrimary: addr.isPrimary,
          location: {
            latitude: addr.latitude,
            longitude: addr.longitude
          },
          addressType: addr.addressType || null
        }))
      };

      // Call the service to create customer
      this.externalDataService.createCustomer(customerData).subscribe({
        next: () => {
          this.isSubmitting = false;
          // Navigate to customer list after successful creation
          this.router.navigate(['/customers']);
        },
        error: (error) => {
          console.error('Error creating customer:', error);
          this.isSubmitting = false;
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.customerForm.controls).forEach(key => {
      const control = this.customerForm.get(key);
      control?.markAsTouched();

      if (control instanceof FormArray) {
        (control as FormArray).controls.forEach(group => {
          if (group instanceof FormGroup) {
            Object.keys(group.controls).forEach(key => {
              const nestedControl = group.get(key);
              nestedControl?.markAsTouched();
            });
          }
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/customers']);
  }

  getErrorMessage(controlName: string, fieldName?: string): string {
    const control = fieldName 
      ? this.customerForm.get(controlName)?.get(fieldName)
      : this.customerForm.get(controlName);

    if (control?.hasError('required')) {
      return `${fieldName || controlName} is required`;
    }
    if (control?.hasError('minlength')) {
      return `${fieldName || controlName} must be at least ${control.errors?.['minlength'].requiredLength} characters`;
    }
    if (control?.hasError('indianPhone')) {
      return control.errors?.['indianPhone'].message;
    }
    if (control?.hasError('pattern')) {
      if (controlName === 'phoneNumber') {
        return 'Please enter a valid Indian mobile number (10 digits starting with 6,7,8,9)';
      }
      if (fieldName === 'pincode') {
        return 'Please enter a valid 6-digit pincode';
      }
    }
    if (control?.hasError('min') || control?.hasError('max')) {
      if (fieldName === 'latitude') {
        return 'Latitude must be between -90 and 90';
      }
      if (fieldName === 'longitude') {
        return 'Longitude must be between -180 and 180';
      }
    }
    return '';
  }
} 